import type { FileStatusType } from '../api/api.types';
import { ProcessingStatusesENUM, UploadStatusesENUM } from '../api/api.types';
import config from '../config';
import type { AggregateFileStatusesType } from '../ui/components/ManageTablesModal/types';

const sleep = (timeout: number) => {
  return new Promise((res) => {
    setTimeout(res, timeout);
  });
};

const getYearOptions = (): string[] => {
  const startYear = config.startYear;
  let currentYear = new Date().getFullYear();
  const years = [];
  while (currentYear >= startYear) {
    years.push(currentYear.toString());
    currentYear -= 1;
  }
  return years;
};

const getUploadingText = (status: UploadStatusesENUM) => {
  switch (status) {
    case UploadStatusesENUM.SUCCESS:
      return 'Uploaded';
    case UploadStatusesENUM.FAILED:
      return 'Failed';
    default:
      return 'Uploading';
  }
};

const getProcessingText = (status: ProcessingStatusesENUM) => {
  switch (status) {
    case ProcessingStatusesENUM.SUCCESS:
      return 'Processed';
    case ProcessingStatusesENUM.FAILED:
      return 'Failed';
    default:
      return 'Processing';
  }
};

const aggregateFileStatuses = (fileStatuses: FileStatusType[]): AggregateFileStatusesType => {
  if (!fileStatuses.length) {
    return {
      uploadingCount: 0,
      processRejectedCount: 0,
      uploadRejectedCount: 0,
      processedCount: 0,
      processingCount: 0,
      isUploading: false,
      isProcessing: false,
    };
  }

  const isUploadingDone = fileStatuses.every((fileStatus) => {
    return (
      fileStatus.uploadStatus === UploadStatusesENUM.SUCCESS ||
      fileStatus.uploadStatus === UploadStatusesENUM.FAILED
    );
  });

  const isProcessingDone = fileStatuses.every((fileStatus) => {
    return (
      fileStatus.processingStatus === ProcessingStatusesENUM.SUCCESS ||
      fileStatus.processingStatus === ProcessingStatusesENUM.FAILED
    );
  });

  const res = fileStatuses.reduce(
    (acc, fileStatus) => {
      switch (fileStatus.uploadStatus) {
        case UploadStatusesENUM.SUCCESS:
          acc.uploadingCount += 1;
          break;
        case UploadStatusesENUM.FAILED:
          acc.uploadRejectedCount += 1;
          break;
        default:
          break;
      }

      switch (fileStatus.processingStatus) {
        case ProcessingStatusesENUM.PENDING:
          acc.processingCount += 1;
          break;
        case ProcessingStatusesENUM.SUCCESS:
          acc.processedCount += 1;
          break;
        case ProcessingStatusesENUM.FAILED:
          acc.processRejectedCount += 1;
          break;
        default:
          break;
      }
      return acc;
    },
    {
      uploadingCount: 0,
      processRejectedCount: 0,
      uploadRejectedCount: 0,
      processedCount: 0,
      processingCount: 0,
    },
  );

  return {
    isUploading: !isUploadingDone,
    isProcessing: !isProcessingDone,
    ...res,
  };
};

export default {
  sleep,
  getYearOptions,
  getUploadingText,
  getProcessingText,
  aggregateFileStatuses,
};
